/**
 * Web side of the text roles (./textRoles): `textRole('label')` is the role as
 * React CSSProperties, its color a token CSS variable (light and dark follow
 * `<html data-theme>`). Pass a second argument to recolor ('secondary', or any
 * CSS color such as `tokenVars.warning`).
 */
import type React from 'react';
import { tokenVars } from './tokens';
import { compactSpec, textRoles, type TextRoleColor, type TextRoleName } from './textRoles';

const ROLE_COLORS: Record<TextRoleColor, string> = {
  primary: tokenVars.text.primary,
  secondary: tokenVars.text.secondary,
  muted: tokenVars.text.muted,
};

export function textRole(name: TextRoleName, color?: TextRoleColor | string): React.CSSProperties {
  const r = textRoles[name];
  const style: React.CSSProperties = {
    fontSize: r.fontSize,
    lineHeight: `${r.lineHeight}px`,
    fontWeight: r.fontWeight,
    color: color ? (ROLE_COLORS[color as TextRoleColor] ?? color) : ROLE_COLORS[r.color],
  };
  if (r.letterSpacingEm) style.letterSpacing = `${r.letterSpacingEm}em`;
  if (r.uppercase) style.textTransform = 'uppercase';
  if (r.tabularNums) style.fontVariantNumeric = 'tabular-nums';
  return style;
}

/**
 * Compact density on the web (./textRoles `compactSpec`). Inline styles cannot
 * hold a media query, so compact controls read these CSS variables, which
 * ./cssVars switches on a phone (at `compactSpec.touchMaxWidth` and
 * narrower): 36px controls and content-width segment tracks on a desktop or
 * iPad, 44px touch targets and full-width tracks on a phone. The fallback in
 * each `var()` is the desktop value, so a page without the theme stylesheet
 * still renders compact.
 */
export const compactVars = {
  /** Height of a compact segment, button, input or select. */
  controlHeight: `var(--tc-compact-control-height, ${compactSpec.controlHeight}px)`,
  /** Width of a compact segmented track: its content on a desktop, the row on a phone. */
  trackWidth: 'var(--tc-compact-track-width, fit-content)',
  /** Flex of a compact segment: its label's width on a desktop (shrinking, with an ellipsis, when the row is too narrow); on a phone it grows to fill its row. */
  segmentFlex: 'var(--tc-compact-segment-flex, 0 1 auto)',
  /** Whether a compact track wraps: one row on a desktop; on a phone segments wrap onto more rows rather than cut their labels. */
  trackWrap: 'var(--tc-compact-track-wrap, nowrap)',
  /** Flex of a compact segment in a wrapping track: its label's width on a desktop, filling its row on a phone. */
  wrapSegmentFlex: 'var(--tc-compact-wrap-segment-flex, 0 0 auto)',
} as const;

/** The phone overrides for `compactVars`, as a stylesheet block (./cssVars). */
export function compactTouchCSS(): string {
  return `@media (max-width: ${compactSpec.touchMaxWidth}px){\n  :root{\n    --tc-compact-control-height: ${compactSpec.touchControlHeight}px;\n    --tc-compact-track-width: 100%;\n    --tc-compact-segment-flex: 1 1 auto;\n    --tc-compact-track-wrap: wrap;\n    --tc-compact-wrap-segment-flex: 1 1 auto;\n  }\n}`;
}
